import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import {
  FileText, Plus, Users, Clock, CheckCircle, AlertCircle,
  ChevronRight, Send, Eye, Loader2, ClipboardCheck, Sparkles, MessageCircle
} from 'lucide-react';

const PUNCTUALITY = [
  { value: 'on_time', label: 'On Time', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'late_excused', label: 'Late (Excused)', color: 'bg-amber-100 text-amber-700' },
  { value: 'late_unexcused', label: 'Late (Unexcused)', color: 'bg-orange-100 text-orange-700' },
  { value: 'absent_excused', label: 'Absent (Excused)', color: 'bg-slate-100 text-slate-600' },
  { value: 'absent_unexcused', label: 'Absent (Unexcused)', color: 'bg-red-100 text-red-700' },
];

const PARTICIPATION = [
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'moderate', label: 'Moderate', color: 'bg-sky-100 text-sky-700' },
  { value: 'minimal', label: 'Minimal', color: 'bg-amber-100 text-amber-700' },
  { value: 'disengaged', label: 'Disengaged', color: 'bg-red-100 text-red-700' },
];

const BEHAVIOR = [
  { value: 'appropriate', label: 'Appropriate', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'needed_redirection', label: 'Needed Redirection', color: 'bg-amber-100 text-amber-700' },
  { value: 'disruptive', label: 'Disruptive', color: 'bg-red-100 text-red-700' },
  { value: 'removed_from_group', label: 'Removed from Group', color: 'bg-red-200 text-red-800' },
];

const RECEPTIVENESS = [
  { value: 'very_receptive', label: 'Very Receptive', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'receptive', label: 'Receptive', color: 'bg-sky-100 text-sky-700' },
  { value: 'neutral', label: 'Neutral', color: 'bg-slate-100 text-slate-600' },
  { value: 'resistant', label: 'Resistant', color: 'bg-red-100 text-red-700' },
];

const PEER_INTERACTION = [
  { value: 'positive', label: 'Positive', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'neutral', label: 'Neutral', color: 'bg-slate-100 text-slate-600' },
  { value: 'negative', label: 'Negative', color: 'bg-orange-100 text-orange-700' },
  { value: 'isolated', label: 'Isolated/Withdrawn', color: 'bg-amber-100 text-amber-700' },
];

function StatusBadge({ status }) {
  const map = {
    pending_assessments: { label: 'Assessments Pending', cls: 'bg-amber-100 text-amber-700' },
    pending_coordinator: { label: 'Awaiting Coordinator', cls: 'bg-sky-100 text-sky-700' },
    pending_ed: { label: 'Awaiting ED Approval', cls: 'bg-violet-100 text-violet-700' },
    approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
    revision_requested: { label: 'Revision Requested', cls: 'bg-red-100 text-red-700' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <Badge className={`${s.cls} border-0 text-[10px]`}>{s.label}</Badge>;
}

function OptionSelector({ options, value, onChange, label }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
              value === opt.value ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
            data-testid={`opt-${label.toLowerCase().replace(/\s/g, '-')}-${opt.value}`}
          >{opt.label}</button>
        ))}
      </div>
    </div>
  );
}

export default function SessionDocumentationPage({ program = 'heat' }) {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my_docs');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [assessingParticipant, setAssessingParticipant] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [modules, setModules] = useState([]);
  const [questionBank, setQuestionBank] = useState([]);
  const [availableReflections, setAvailableReflections] = useState([]);
  const [reflectionsToAttach, setReflectionsToAttach] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');

  const [form, setForm] = useState({
    module_name: '', session_date: new Date().toISOString().slice(0, 10),
    session_type: 'group', group_note: '', topic_covered: '', objectives: '',
    attendee_ids: [],
  });

  const [assessForm, setAssessForm] = useState({
    punctuality: 'on_time', participation: 'moderate', behavior: 'appropriate',
    receptiveness: 'receptive', peer_interaction: 'neutral', notes: '',
    individualized_doc: { question_id: '', question_text: '', answer: '' },
  });

  const isFacilitator = user.role === 'facilitator';
  const isCoordinator = user.role === 'coordinator';
  const isAdmin = user.role === 'admin';
  const canApprove = isCoordinator || isAdmin;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, partsRes, modsRes, qbRes] = await Promise.all([
        api.get(`/session-docs?program=${program}`).catch(() => ({ data: [] })),
        api.get(`/${program}/participants`).catch(() => ({ data: [] })),
        api.get(`/${program}/modules`).catch(() => ({ data: [] })),
        api.get('/session-docs/question-bank').catch(() => ({ data: [] })),
      ]);
      setDocs(docsRes.data || []);
      setParticipants(partsRes.data || []);
      setModules(modsRes.data || []);
      setQuestionBank(qbRes.data || []);
    } catch {
      toast.error('Failed to load session documents');
    }
    setLoading(false);
  }, [program]);

  useEffect(() => { loadData(); }, [loadData]);

  // When a coordinator/ED selects a doc, fetch the reflections available to attach
  useEffect(() => {
    if (selectedDoc && canApprove) {
      api.get(`/session-docs/${selectedDoc.id}/available-reflections`)
        .then(r => setAvailableReflections(r.data || []))
        .catch(() => setAvailableReflections([]));
      // Pre-seed from doc.include_reflections_for if previously set
      setReflectionsToAttach(selectedDoc.include_reflections_for || []);
    } else {
      setAvailableReflections([]);
      setReflectionsToAttach([]);
    }
  }, [selectedDoc?.id, canApprove]);

  const pendingReview = useMemo(() => docs.filter(d =>
    (isCoordinator && d.approval_status === 'pending_coordinator') ||
    (isAdmin && d.approval_status === 'pending_ed')
  ), [docs, isCoordinator, isAdmin]);

  const toggleAttendee = (id) => {
    setForm(f => ({
      ...f,
      attendee_ids: f.attendee_ids.includes(id)
        ? f.attendee_ids.filter(a => a !== id)
        : [...f.attendee_ids, id]
    }));
  };

  const createDoc = async () => {
    if (!form.group_note.trim()) return toast.error('Group note is required');
    if (!form.attendee_ids.length) return toast.error('Select at least one participant');
    if (!form.module_name) return toast.error('Module/topic name is required');
    setSubmitting(true);
    try {
      await api.post('/session-docs', { ...form, program });
      toast.success('Session document created! Now complete individual assessments.');
      setCreateOpen(false);
      setForm({ module_name: '', session_date: new Date().toISOString().slice(0, 10), session_type: 'group', group_note: '', topic_covered: '', objectives: '', attendee_ids: [] });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
    setSubmitting(false);
  };

  const submitAssessment = async () => {
    if (!selectedDoc || !assessingParticipant) return;
    if (!assessForm.individualized_doc.question_id) {
      return toast.error('Pick one individualized question for this participant.');
    }
    if (!assessForm.individualized_doc.answer.trim()) {
      return toast.error('Answer the individualized question.');
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/session-docs/${selectedDoc.id}/assess/${assessingParticipant.user_id}`, assessForm);
      toast.success(res.data.message);
      setAssessingParticipant(null);
      setAssessForm({
        punctuality: 'on_time', participation: 'moderate', behavior: 'appropriate',
        receptiveness: 'receptive', peer_interaction: 'neutral', notes: '',
        individualized_doc: { question_id: '', question_text: '', answer: '' },
      });
      // Refresh the selected doc
      const updated = await api.get(`/session-docs/${selectedDoc.id}`);
      setSelectedDoc(updated.data);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save assessment');
    }
    setSubmitting(false);
  };

  const approveDoc = async (docId, status, notes = '') => {
    try {
      await api.post(`/session-docs/${docId}/approve`, {
        status,
        notes,
        include_reflections_for: status === 'approved' ? reflectionsToAttach : [],
      });
      toast.success(status === 'approved' ? 'Document approved!' : 'Revision requested');
      setSelectedDoc(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openAssessDialog = (att) => {
    setAssessingParticipant(att);
    // If editing existing assessment, prefill — else reset
    if (att.assessment) {
      setAssessForm({
        punctuality: att.assessment.punctuality || 'on_time',
        participation: att.assessment.participation || 'moderate',
        behavior: att.assessment.behavior || 'appropriate',
        receptiveness: att.assessment.receptiveness || 'receptive',
        peer_interaction: att.assessment.peer_interaction || 'neutral',
        notes: att.assessment.notes || '',
        individualized_doc: att.assessment.individualized_doc || att.individualized_doc || { question_id: '', question_text: '', answer: '' },
      });
    } else {
      setAssessForm({
        punctuality: 'on_time', participation: 'moderate', behavior: 'appropriate',
        receptiveness: 'receptive', peer_interaction: 'neutral', notes: '',
        individualized_doc: { question_id: '', question_text: '', answer: '' },
      });
    }
  };

  const onPickQuestion = (qid, participantName) => {
    const q = questionBank.find(qq => qq.id === qid);
    if (!q) return;
    const text = q.text.replace(/\{name\}/g, participantName || 'this participant');
    setAssessForm(f => ({
      ...f,
      individualized_doc: { question_id: qid, question_text: text, answer: f.individualized_doc?.answer || '' },
    }));
  };

  const toggleReflectionAttach = (userId) => {
    setReflectionsToAttach(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  // Detail view of a single session doc
  if (selectedDoc) {
    const allAssessed = selectedDoc.attendees?.every(a => a.assessment_completed);
    return (
      <div className="space-y-4" data-testid="session-doc-detail">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setSelectedDoc(null)} className="gap-1 text-sm"><ChevronRight size={14} className="rotate-180" /> Back to Documents</Button>
          {selectedDoc.approval_status === 'approved' && (
            <Button
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/session-docs/${selectedDoc.id}/export-pdf`, '_blank')}
              variant="outline"
              className="gap-1.5 rounded-full text-sm"
              data-testid="export-pdf-btn"
            >
              <FileText size={14} /> Export PDF Packet
            </Button>
          )}
        </div>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{selectedDoc.module_name}</CardTitle>
              <StatusBadge status={selectedDoc.approval_status} />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><span className="text-muted-foreground">Date:</span> <strong>{selectedDoc.session_date}</strong></div>
              <div><span className="text-muted-foreground">Type:</span> <strong className="capitalize">{selectedDoc.session_type}</strong></div>
              <div><span className="text-muted-foreground">Facilitator:</span> <strong>{selectedDoc.facilitator_name}</strong></div>
              <div><span className="text-muted-foreground">Attendees:</span> <strong>{selectedDoc.attendee_count}</strong></div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50">
              <p className="text-xs font-medium text-muted-foreground mb-1">Group Note</p>
              <p className="text-sm whitespace-pre-line">{selectedDoc.group_note}</p>
            </div>
            {selectedDoc.topic_covered && <p className="text-xs"><span className="text-muted-foreground">Topic:</span> {selectedDoc.topic_covered}</p>}
            <div className="flex items-center gap-2">
              <Progress value={selectedDoc.attendee_count ? (selectedDoc.assessments_completed / selectedDoc.attendee_count) * 100 : 0} className="h-2 flex-1" />
              <span className="text-xs text-muted-foreground">{selectedDoc.assessments_completed}/{selectedDoc.attendee_count} assessed</span>
            </div>
          </CardContent>
        </Card>

        {/* Participant Assessment List */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Individual Participant Assessments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(selectedDoc.attendees || []).map(att => (
              <div key={att.user_id} className={`p-3 rounded-xl border transition-all ${att.assessment_completed ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-slate-200 hover:border-teal-300'}`} data-testid={`attendee-${att.user_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${att.assessment_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {att.full_name?.[0] || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{att.full_name}</p>
                      {att.assessment_completed && att.assessment && (
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {PUNCTUALITY.find(p => p.value === att.assessment.punctuality) && <Badge className={`${PUNCTUALITY.find(p => p.value === att.assessment.punctuality).color} border-0 text-[9px]`}>{PUNCTUALITY.find(p => p.value === att.assessment.punctuality).label}</Badge>}
                          {PARTICIPATION.find(p => p.value === att.assessment.participation) && <Badge className={`${PARTICIPATION.find(p => p.value === att.assessment.participation).color} border-0 text-[9px]`}>{PARTICIPATION.find(p => p.value === att.assessment.participation).label}</Badge>}
                          {BEHAVIOR.find(p => p.value === att.assessment.behavior) && <Badge className={`${BEHAVIOR.find(p => p.value === att.assessment.behavior).color} border-0 text-[9px]`}>{BEHAVIOR.find(p => p.value === att.assessment.behavior).label}</Badge>}
                        </div>
                      )}
                    </div>
                  </div>
                  {att.assessment_completed ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle size={10} className="mr-0.5" /> Done</Badge>
                  ) : isFacilitator ? (
                    <Button size="sm" className="rounded-full bg-teal-600 hover:bg-teal-700 text-white gap-1 text-xs" onClick={() => openAssessDialog(att)} data-testid={`assess-btn-${att.user_id}`}>
                      <ClipboardCheck size={12} /> Assess
                    </Button>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]"><Clock size={10} className="mr-0.5" /> Pending</Badge>
                  )}
                </div>
                {att.individualized_doc?.answer && (
                  <div className="mt-2 ml-12 p-2 rounded-lg bg-violet-50 border-l-2 border-violet-300">
                    <p className="text-[10px] font-semibold text-violet-700 mb-0.5 uppercase tracking-wide">{att.individualized_doc.question_text || 'Individualized Note'}</p>
                    <p className="text-xs text-slate-700">{att.individualized_doc.answer}</p>
                  </div>
                )}
                {att.reflection_attached && att.participant_reflection && (
                  <div className="mt-2 ml-12 p-2 rounded-lg bg-amber-50 border-l-2 border-amber-400">
                    <p className="text-[10px] font-semibold text-amber-700 mb-0.5 uppercase tracking-wide flex items-center gap-1"><Sparkles size={10} /> Participant Reflection (Attached)</p>
                    <p className="text-xs text-slate-700 italic">"{att.participant_reflection.reflection_text}"</p>
                    {att.participant_reflection.ai_follow_up_question && (
                      <>
                        <p className="text-[10px] font-semibold text-amber-700 mt-1.5">AI follow-up:</p>
                        <p className="text-xs text-slate-600">{att.participant_reflection.ai_follow_up_question}</p>
                      </>
                    )}
                    {att.participant_reflection.follow_up_response && (
                      <>
                        <p className="text-[10px] font-semibold text-amber-700 mt-1.5">Their response:</p>
                        <p className="text-xs text-slate-700">{att.participant_reflection.follow_up_response}</p>
                      </>
                    )}
                  </div>
                )}
                {att.assessment?.notes && <p className="text-xs text-slate-600 mt-1.5 pl-12">Note: {att.assessment.notes}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Reflection Attachment Panel — Coordinator/ED only, before approval */}
        {canApprove && (selectedDoc.approval_status === 'pending_coordinator' || selectedDoc.approval_status === 'pending_ed') && availableReflections.length > 0 && (
          <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400" data-testid="reflection-attach-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Sparkles size={14} className="text-amber-500" /> Attach Participant Reflections to Final Doc</CardTitle>
              <p className="text-xs text-muted-foreground">Optionally include each participant's own reflection from this class in the approved documentation.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {availableReflections.map(r => (
                <div key={r.id} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-amber-50/40 transition-colors">
                  <Checkbox
                    id={`refl-${r.user_id}`}
                    checked={reflectionsToAttach.includes(r.user_id)}
                    onCheckedChange={() => toggleReflectionAttach(r.user_id)}
                    className="mt-1"
                    data-testid={`attach-reflection-${r.user_id}`}
                  />
                  <label htmlFor={`refl-${r.user_id}`} className="flex-1 cursor-pointer">
                    <p className="text-xs font-semibold">{r.user_name}</p>
                    <p className="text-xs text-slate-600 mt-0.5 italic line-clamp-2">"{r.reflection_text}"</p>
                    {r.ai_follow_up_question && (
                      <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><MessageCircle size={9} /> AI: {r.ai_follow_up_question}</p>
                    )}
                  </label>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                {reflectionsToAttach.length} of {availableReflections.length} reflection{availableReflections.length !== 1 ? 's' : ''} selected to attach
              </p>
            </CardContent>
          </Card>
        )}

        {/* Approval Actions */}
        {canApprove && (selectedDoc.approval_status === 'pending_coordinator' || selectedDoc.approval_status === 'pending_ed') && (
          <Card className="border-0 shadow-sm rounded-2xl border-t-4 border-t-violet-500">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-3">{isAdmin ? 'Executive Director Approval' : 'Coordinator Review'}</p>
              <div className="flex gap-2">
                <Button onClick={() => approveDoc(selectedDoc.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1 flex-1" data-testid="approve-doc-btn">
                  <CheckCircle size={14} /> Approve & Link to Participants
                </Button>
                <Button onClick={() => approveDoc(selectedDoc.id, 'revision_requested', 'Please review and resubmit')} variant="outline" className="rounded-full gap-1" data-testid="revision-doc-btn">
                  <AlertCircle size={14} /> Request Revision
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assessment Dialog */}
        <Dialog open={!!assessingParticipant} onOpenChange={(o) => !o && setAssessingParticipant(null)}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assess: {assessingParticipant?.full_name}</DialogTitle>
              <DialogDescription>Rate participant performance and document one individualized observation</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <OptionSelector label="Punctuality" options={PUNCTUALITY} value={assessForm.punctuality} onChange={v => setAssessForm({...assessForm, punctuality: v})} />
              <OptionSelector label="Participation Level" options={PARTICIPATION} value={assessForm.participation} onChange={v => setAssessForm({...assessForm, participation: v})} />
              <OptionSelector label="Behavior" options={BEHAVIOR} value={assessForm.behavior} onChange={v => setAssessForm({...assessForm, behavior: v})} />
              <OptionSelector label="Receptiveness to Material" options={RECEPTIVENESS} value={assessForm.receptiveness} onChange={v => setAssessForm({...assessForm, receptiveness: v})} />
              <OptionSelector label="Peer Interaction" options={PEER_INTERACTION} value={assessForm.peer_interaction} onChange={v => setAssessForm({...assessForm, peer_interaction: v})} />

              {/* Individualized Question Bank — REQUIRED */}
              <div className="space-y-2 p-3 rounded-xl bg-violet-50 border border-violet-200">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Individualized Question (required — pick one)
                </Label>
                <Select
                  value={assessForm.individualized_doc.question_id}
                  onValueChange={(v) => onPickQuestion(v, assessingParticipant?.full_name)}
                >
                  <SelectTrigger data-testid="individualized-question-select" className="bg-white">
                    <SelectValue placeholder="Choose a question to answer about this participant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {questionBank.map(q => (
                      <SelectItem key={q.id} value={q.id} className="text-xs">
                        {q.text.replace(/\{name\}/g, assessingParticipant?.full_name || 'this participant')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assessForm.individualized_doc.question_id && (
                  <DictationInput
                    value={assessForm.individualized_doc.answer}
                    onChange={v => setAssessForm(f => ({ ...f, individualized_doc: { ...f.individualized_doc, answer: v } }))}
                    rows={3}
                    placeholder="Your answer to the question above..."
                    showCleanup={false}
                    data-testid="individualized-answer"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Additional Notes (Optional)</Label>
                <DictationInput value={assessForm.notes} onChange={v => setAssessForm({...assessForm, notes: v})} rows={2} placeholder="Any incidents, breakthroughs, or concerns..." showCleanup={false} data-testid="assess-notes" />
              </div>
              <Button onClick={submitAssessment} disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1" data-testid="submit-assessment-btn">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Save Assessment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Document List View
  return (
    <div className="space-y-6" data-testid="session-docs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Session Documentation</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Document group sessions and assess each participant</p>
        </div>
        {isFacilitator && (
          <Button onClick={() => setCreateOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="create-session-doc-btn">
            <Plus size={14} /> New Session Doc
          </Button>
        )}
      </div>

      {/* Pending Review Banner */}
      {canApprove && pendingReview.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl bg-violet-50 border-l-4 border-l-violet-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-violet-600" />
              <span className="text-sm font-medium text-violet-900">{pendingReview.length} document{pendingReview.length > 1 ? 's' : ''} awaiting your review</span>
            </div>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTab('review')}>Review Now</Button>
          </CardContent>
        </Card>
      )}

      {canApprove && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-emerald-400" data-testid="bulk-export-card">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex-1">
              <p className="text-sm font-semibold flex items-center gap-1.5"><FileText size={14} className="text-emerald-600" /> Quarterly Funder Report Export</p>
              <p className="text-[11px] text-muted-foreground">Download all approved session docs in a date range as a single ZIP.</p>
            </div>
            <Input type="date" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} className="h-8 text-xs w-36" data-testid="bulk-export-start" />
            <Input type="date" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} className="h-8 text-xs w-36" data-testid="bulk-export-end" />
            <Button
              size="sm"
              variant="outline"
              className="rounded-full gap-1"
              onClick={() => {
                const params = new URLSearchParams();
                params.set('program', program);
                if (bulkStart) params.set('start_date', bulkStart);
                if (bulkEnd) params.set('end_date', bulkEnd);
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/session-docs-export/bulk-zip?${params}`, '_blank');
              }}
              data-testid="bulk-export-btn"
            >
              <FileText size={12} /> Download ZIP
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="my_docs" className="rounded-full data-[state=active]:bg-white text-xs gap-1"><FileText size={12} /> {isFacilitator ? 'My Documents' : 'All Documents'}</TabsTrigger>
          {canApprove && <TabsTrigger value="review" className="rounded-full data-[state=active]:bg-white text-xs gap-1"><Eye size={12} /> Pending Review ({pendingReview.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="my_docs" className="space-y-2 mt-4">
          {docs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><FileText size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">No session documents yet.</p></div>
          ) : docs.map(d => (
            <Card key={d.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedDoc(d)} data-testid={`session-doc-${d.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold">{d.module_name}</h3>
                      <StatusBadge status={d.approval_status} />
                      <Badge variant="outline" className="text-[10px] uppercase">{d.program}</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>{d.session_date}</span>
                      <span>{d.attendee_count} attendees</span>
                      <span>{d.assessments_completed}/{d.attendee_count} assessed</span>
                      <span className="capitalize">{d.session_type}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground/40 shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {canApprove && (
          <TabsContent value="review" className="space-y-2 mt-4">
            {pendingReview.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground"><CheckCircle size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">All caught up! No documents pending review.</p></div>
            ) : pendingReview.map(d => (
              <Card key={d.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-violet-400" onClick={() => setSelectedDoc(d)} data-testid={`review-doc-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold">{d.module_name}</h3>
                        <StatusBadge status={d.approval_status} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">By {d.facilitator_name} - {d.session_date} - {d.attendee_count} attendees</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full text-xs">Review</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Session Doc Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Session Documentation</DialogTitle>
            <DialogDescription>Document a group session and select attending participants</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Module / Topic Name</Label>
                <Select value={form.module_name} onValueChange={v => setForm({...form, module_name: v})}>
                  <SelectTrigger data-testid="module-select"><SelectValue placeholder="Select module" /></SelectTrigger>
                  <SelectContent>
                    {modules.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                    <SelectItem value="custom">Custom Topic...</SelectItem>
                  </SelectContent>
                </Select>
                {form.module_name === 'custom' && <Input placeholder="Enter topic name" onChange={e => setForm({...form, module_name: e.target.value})} className="mt-1" />}
              </div>
              <div className="space-y-1.5">
                <Label>Session Date</Label>
                <Input type="date" value={form.session_date} onChange={e => setForm({...form, session_date: e.target.value})} data-testid="session-date" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Group Note</Label>
              <DictationInput value={form.group_note} onChange={v => setForm({...form, group_note: v})} rows={4} placeholder="Document what was covered, key discussions, group dynamics, and any observations..." data-testid="group-note" promptContext={{ context: 'session_note', taskName: form.module_name, taskDescription: 'Group session documentation for justice-involved participants', category: 'session' }} />
            </div>

            <div className="space-y-1.5">
              <Label>Topic Covered (Brief Summary)</Label>
              <Input value={form.topic_covered} onChange={e => setForm({...form, topic_covered: e.target.value})} placeholder="e.g., Criminal Thinking Errors - Module 3" />
            </div>

            {/* Attendee Selection */}
            <div className="space-y-1.5">
              <Label>Select Attendees ({form.attendee_ids.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto border rounded-xl p-2 space-y-1">
                {participants.length > 0 ? participants.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleAttendee(p.id)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                      form.attendee_ids.includes(p.id) ? 'bg-teal-50 border border-teal-300 text-teal-800' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`attendee-toggle-${p.id}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${form.attendee_ids.includes(p.id) ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {form.attendee_ids.includes(p.id) ? <CheckCircle size={14} /> : (p.full_name?.[0] || '?')}
                    </div>
                    <span>{p.full_name}</span>
                  </button>
                )) : <p className="text-xs text-muted-foreground text-center py-2">No participants found</p>}
              </div>
            </div>

            <Button onClick={createDoc} disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1" data-testid="submit-session-doc-btn">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Create Session Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
